import { useCallback } from 'react';
import { trpc } from '@/lib/trpc';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import type {
  MatchField,
  InheritMode,
  CreateExclusionRuleBatchInput,
  UpdateExclusionRuleInput,
} from '@threadheads/shared';

const EXCLUSION_STALE_TIME_MS = 60_000;

/**
 * Hook bundling all line-item-exclusion tRPC calls for the Settings UI.
 *
 * `staleTime: 60s` on every read query means opening the Production Settings
 * accordion twice in quick succession reuses the cached result instead of
 * refetching. Mutations invalidate only the specific query they mutate
 * (no shotgun `invalidateAll`).
 */
export function useLineItemExclusions(locationId: string | null) {
  const utils = trpc.useUtils();
  const { toast } = useToast();
  const { t } = useTranslation('settings');

  const orgRulesQuery = trpc.lineItemExclusions.listOrgRules.useQuery(undefined, {
    staleTime: EXCLUSION_STALE_TIME_MS,
  });
  const locationsWithOverridesQuery =
    trpc.lineItemExclusions.listLocationsWithOverrides.useQuery(undefined, {
      staleTime: EXCLUSION_STALE_TIME_MS,
    });
  const overrideSummariesQuery =
    trpc.lineItemExclusions.listLocationOverrideSummaries.useQuery(undefined, {
      staleTime: EXCLUSION_STALE_TIME_MS,
    });
  const locationRulesQuery = trpc.lineItemExclusions.listLocationRules.useQuery(
    { location_id: locationId ?? '' },
    { enabled: !!locationId, staleTime: EXCLUSION_STALE_TIME_MS },
  );
  const locationSettingsQuery = trpc.lineItemExclusions.getLocationSettings.useQuery(
    { location_id: locationId ?? '' },
    { enabled: !!locationId, staleTime: EXCLUSION_STALE_TIME_MS },
  );

  // Stable callback — deps only on the utils object and locationId so
  // consumers can safely list `fetchPreview` in useEffect deps without
  // causing per-render refetches.
  const fetchPreview = useCallback(
    (args: { match_field: MatchField; value: string; location_id: string | null }) =>
      utils.lineItemExclusions.preview.fetch(args),
    [utils.lineItemExclusions.preview],
  );

  const invalidateRulesOnly = (scope: 'org' | 'location') => {
    if (scope === 'org') {
      void utils.lineItemExclusions.listOrgRules.invalidate();
    } else {
      void utils.lineItemExclusions.listLocationRules.invalidate();
      void utils.lineItemExclusions.listLocationsWithOverrides.invalidate();
      void utils.lineItemExclusions.listLocationOverrideSummaries.invalidate();
    }
  };

  const createBatch = trpc.lineItemExclusions.createBatch.useMutation({
    onSuccess: (result, variables) => {
      invalidateRulesOnly(variables.location_id ? 'location' : 'org');
      toast({
        title: t('exclusions.toast.added', { count: result.inserted }),
        description: result.skipped > 0
          ? t('exclusions.toast.skippedDuplicates', { count: result.skipped })
          : undefined,
      });
    },
    onError: (err) => {
      toast({
        variant: 'destructive',
        title: t('exclusions.toast.addFailed'),
        description: err.message,
      });
    },
  });

  const update = trpc.lineItemExclusions.update.useMutation({
    onSuccess: () => {
      void utils.lineItemExclusions.listOrgRules.invalidate();
      void utils.lineItemExclusions.listLocationRules.invalidate();
      toast({ title: t('exclusions.toast.updated') });
    },
    onError: (err) => {
      toast({
        variant: 'destructive',
        title: t('exclusions.toast.updateFailed'),
        description: err.message,
      });
    },
  });

  const deleteRule = trpc.lineItemExclusions.delete.useMutation({
    onSuccess: () => {
      void utils.lineItemExclusions.listOrgRules.invalidate();
      void utils.lineItemExclusions.listLocationRules.invalidate();
      toast({ title: t('exclusions.toast.deleted') });
    },
    onError: (err) => {
      toast({
        variant: 'destructive',
        title: t('exclusions.toast.deleteFailed'),
        description: err.message,
      });
    },
  });

  const updateLocationSettings = trpc.lineItemExclusions.updateLocationSettings.useMutation({
    onSuccess: () => {
      void utils.lineItemExclusions.getLocationSettings.invalidate();
      void utils.lineItemExclusions.listLocationsWithOverrides.invalidate();
      void utils.lineItemExclusions.listLocationOverrideSummaries.invalidate();
      toast({ title: t('exclusions.toast.settingsSaved') });
    },
    onError: (err) => {
      toast({
        variant: 'destructive',
        title: t('exclusions.toast.settingsFailed'),
        description: err.message,
      });
    },
  });

  return {
    orgRules: orgRulesQuery.data ?? [],
    orgRulesLoading: orgRulesQuery.isLoading,
    orgRulesError: orgRulesQuery.isError,
    locationsWithOverrides:
      locationsWithOverridesQuery.data?.locationIds ?? ([] as string[]),
    overrideSummaries: overrideSummariesQuery.data?.summaries ?? [],
    locationRules: locationRulesQuery.data ?? [],
    locationRulesLoading: locationRulesQuery.isLoading,
    inheritMode: (locationSettingsQuery.data?.inherit_mode as InheritMode | undefined) ?? 'extend',
    inheritModeLoading: locationSettingsQuery.isLoading,

    createBatchAsync: (input: CreateExclusionRuleBatchInput) => createBatch.mutateAsync(input),
    createBatchPending: createBatch.isPending,

    toggleActive: (ruleId: string, isActive: boolean) =>
      update.mutateAsync({ id: ruleId, is_active: isActive } satisfies UpdateExclusionRuleInput),
    togglePending: update.isPending,
    togglingRuleId: update.variables?.id,

    deleteRule: (ruleId: string) => deleteRule.mutateAsync({ id: ruleId }),
    deletePending: deleteRule.isPending,

    fetchPreview,

    setInheritMode: (mode: InheritMode) => {
      if (!locationId) return Promise.resolve();
      return updateLocationSettings.mutateAsync({
        location_id: locationId,
        inherit_mode: mode,
      });
    },
    inheritModeSaving: updateLocationSettings.isPending,
  };
}

/**
 * Live Shopify search wrappers. Debounce in the component.
 */
export function useShopifyTagSearch(query: string, enabled: boolean) {
  return trpc.lineItemExclusions.searchProductTags.useQuery(
    { query, limit: 25 },
    { enabled, staleTime: 30_000 },
  );
}

export function useShopifyProductTypeSearch(query: string, enabled: boolean) {
  return trpc.lineItemExclusions.searchProductTypes.useQuery(
    { query, limit: 25 },
    { enabled, staleTime: 30_000 },
  );
}

export function useShopifyProductSearch(query: string, enabled: boolean) {
  return trpc.prePrint.searchShopifyProducts.useQuery(
    { search: query },
    { enabled: enabled && query.length >= 2, staleTime: 30_000 },
  );
}

/**
 * Variant-SKU search — uses the dedicated productVariants endpoint so admins
 * can find a variant by its SKU directly.
 */
export function useShopifyVariantSearch(query: string, enabled: boolean) {
  return trpc.lineItemExclusions.searchShopifyVariants.useQuery(
    { query, limit: 25 },
    { enabled, staleTime: 30_000 },
  );
}

export type { MatchField };
